import React, { useEffect, useState } from 'react';
import { Text, Image } from '@ui/atoms';
import Button from '@ui/atoms/design-system/Button';
import { appDownloadLink, mobileDeviceTypes } from '@helpers/constants';
import { getMobileOS } from '@helpers/utils';
import useWindowDimensions from '@ui/hooks/useWindowDimensions';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';
import { buttonSizes, buttonTypes } from '@ui/atoms/utils/enums';
import DownloadAppQrCode from '@molecules/DownloadAppQrCode';
import {
    logEvent,
    moengageEventConstants,
} from "@services/moengage";

const AppNudge = ({
    variant = 'card', // 'card', 'navigation', 'inline'
    gaEventName,
    source,
    title,
    subtitle,
    showAnimation = false,
    className = '',
    onClose,
    ...props
}) => {
    const [mobileOs, setMobileOs] = useState(null);
    const [isAnimated, setIsAnimated] = useState(false);
    const [showAppDownloadPrompt, setShowAppDownloadPrompt] = useState(false);
    const { width } = useWindowDimensions();
    const isMobile = width < 1024;
    const isMobileDevice =
        mobileOs === mobileDeviceTypes.android ||
        mobileOs === mobileDeviceTypes.ios;
    const shouldShowMobileView = isMobile && isMobileDevice;

    useEffect(() => {
        setMobileOs(getMobileOS());
    }, [isMobile]);

    useEffect(() => {
        if (showAnimation) {
            const timeout = setTimeout(() => setIsAnimated(true), 600);
            return () => clearTimeout(timeout);
        }
    }, [showAnimation]);

    const getIconComponent = ({ className: iconClassName = "w-4 h-4", forVariant = variant } = {}) => {
        if (mobileOs === mobileDeviceTypes.android) {
            return (
                forVariant === 'navigation' ? (
                    <Image
                        src="/images/logos/google-play-logo-primary-400.svg"
                        alt="Download App"
                        className={iconClassName}
                    />
                ) : (
                    <Image
                        src="/images/playstore-logo.svg"
                        alt="Google Play"
                        className={iconClassName}
                    />
                )
            );
        }
        if (mobileOs === mobileDeviceTypes.ios) {
            return (
                forVariant === 'navigation' ? (
                    <Image
                        src="/images/logos/apple-logo-primary-400.svg"
                        alt="App Store"
                        className={iconClassName}
                    />
                ) : (
                    <Image
                        src="/images/apple-logo.svg"
                        alt="App Store"
                        className={iconClassName}
                    />)
            );
        }
        // For navigation variant: use generic download icon when mobile OS is null
        // For other variants: default to Play Store logo
        if (forVariant === 'navigation') {
            return (
                <Image
                    src="/download_app_default.svg"
                    alt="Download App"
                    className={iconClassName}
                />
            );
        }
        return (
            <Image
                src="/images/playstore-logo.svg"
                alt="Download App"
                className={iconClassName}
            />
        );
    };

    const getButtonText = () => {
        if (!isMobile) {
            return 'Get the App';
        }
        if (mobileOs === mobileDeviceTypes.android) {
            return 'Get Android App';
        }
        if (mobileOs === mobileDeviceTypes.ios) {
            return 'Get iOS App';
        }
        return 'Download app now';
    };

    const handleDownloadApp = () => {
        triggerCustomGaEvent(gaEventName, {
            source,
        });
        let moengageEventName;
        switch (source) {
            case 'mobile_navigation':
                moengageEventName = moengageEventConstants.CLICKED_DOWNLOAD_APP_NUDGE_ON_PROFILE_LOGOUT;
                break;
            default:
                moengageEventName = moengageEventConstants.CLICKED_DOWNLOAD_APP_NUDGE_AFTER_SUCCESSFUL_INVESTMENT;
                break;
        }
        logEvent({
            eventName: moengageEventName,
        });
        if (isMobile) {
            window.open(appDownloadLink, '_blank');
        } else {
            setShowAppDownloadPrompt(true);
        }

        // Call onClose if provided (for closing navigation menu, etc.)
        if (onClose) {
            onClose();
        }
    };

    const getBackgroundStyle = () => {
        if (variant === 'card') {
            if (shouldShowMobileView) {
                return { backgroundColor: '#FFF' };
            } else {
                return { backgroundColor: '#F9F9FB' };
            }
        }
        return {};
    };

    const ButtonPrefixComponent = () => {
        if (!isMobile) {
            return null;
        }
        // For card variant: don't show any icon when mobile OS is null
        if (variant === 'card' && mobileOs === null) {
            return null;
        }
        return getIconComponent({ className: "w-4 h-4", forVariant: variant });
    };

    // Card variant - full featured with animation and background
    if (variant === 'card') {
        return (
            <>
                <div
                    className={`flex overflow-hidden relative gap-3 items-start self-stretch p-3 rounded-xl md:p-4 md:rounded-2xl ${className}`}
                    style={getBackgroundStyle()}
                >
                    {showAnimation && (
                        <div
                            className={`flex absolute justify-center items-center transition-all duration-500 ease-in-out origin-bottom-right ${isAnimated
                                    ? 'bottom-auto left-auto rotate-0 md:rotate-[15deg] md:left-[-12.5px] md:bottom-[-36px]'
                                    : '-rotate-[78deg] bottom-[-7px] left-[30.5px] md:bottom-[-105px] md:left-[-30.5px] md:rotate-[15deg]'
                                }`}
                        >
                            <Image
                                src="/images/MobileAppDownload.svg"
                                alt="app-nudge-illustration"
                                className="w-full h-full"
                            />
                        </div>
                    )}
                    <div className={`flex flex-col gap-3 justify-center w-full md:justify-between md:flex-row ${showAnimation ? 'ml-20 md:ml-14' : ''}`}>
                        <div className="flex flex-col justify-center items-center">
                            <Text
                                content={title || 'Manage your investments easily on <br class="hidden md:block"/>Jiraaf\'s mobile app'}
                                className="self-stretch text-gray-600 h7-semibold"
                                isInnerHtml
                            />
                            {subtitle && (
                                <Text
                                    content={subtitle}
                                    className="self-stretch text-gray-500 p5-regular md:hidden"
                                />
                            )}
                        </div>
                        <Button
                            buttonText={getButtonText()}
                            buttonType={buttonTypes.TEXT_UNDERLINE}
                            buttonSize={buttonSizes.SMALL}
                            prefixComponent={isMobile ? <ButtonPrefixComponent /> : null}
                            onClick={handleDownloadApp}
                        />
                    </div>
                </div>
                {showAppDownloadPrompt && (
                    <DownloadAppQrCode
                        show={showAppDownloadPrompt}
                        onClose={() => setShowAppDownloadPrompt(false)}
                    />
                )}
            </>
        );
    }

    // Navigation variant - simple menu item style
    if (variant === 'navigation') {
        return (
            <>
                <div
                    className={`flex items-center gap-3 cursor-pointer p-2 rounded-xl hover:bg-gray-50 transition-colors ${className}`}
                    onClick={handleDownloadApp}
                    role="button"
                    tabIndex={0}
                    aria-label="Download Jiraaf App"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDownloadApp();
                        }
                    }}
                >
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-50 rounded-lg p-2">
                        {getIconComponent({ className: "w-6 h-6", forVariant: 'navigation' })}
                    </div>
                    <Text
                        content={title || "Download Jiraaf App"}
                        className="p4-medium text-gray-800 flex-1"
                    />
                </div>
                {showAppDownloadPrompt && (
                    <DownloadAppQrCode
                        show={showAppDownloadPrompt}
                        onClose={() => setShowAppDownloadPrompt(false)}
                    />
                )}
            </>
        );
    }

    // Inline variant - simple button style
    if (variant === 'inline') {
        return (
            <>
                <div className={`flex items-center gap-3 ${className}`}>
                    {title && (
                        <Text
                            content={title}
                            className="text-gray-600 p4-medium"
                        />
                    )}
                    <Button
                        buttonText={getButtonText()}
                        buttonType={buttonTypes.PRIMARY}
                        buttonSize={buttonSizes.SMALL}
                        prefixComponent={isMobile ? <ButtonPrefixComponent /> : null}
                        onClick={handleDownloadApp}
                    />
                </div>
                {showAppDownloadPrompt && (
                    <DownloadAppQrCode
                        show={showAppDownloadPrompt}
                        onClose={() => setShowAppDownloadPrompt(false)}
                    />
                )}
            </>
        );
    }

    return null;
};

export default AppNudge; 